import React, { useState, useEffect, useRef } from "react";
import ApiService from "../../services/ApiService";
import getUser from "../../services/decodeJWT";
import { getTalk } from "../../services/D_ID/getTalk";
import Instructions from "../../services/Instructions";

const ModalVideocall = ({ cloneData }) => {

    const [isHovered, setIsHovered] = useState(false);
    const [profileData, setProfileData] = useState({});
    const [poster, setPoster] = useState('');
    const [transcript, setTranscript] = useState("");
    const [isListening, setIsListening] = useState(false);
    const [isCallEnded, setIsCallEnded] = useState(false);
    const [feedbackText, setFeedbackText] = useState("");
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState('');
    const [audio, setAudio] = useState('');
    const [video, setVideo] = useState('');
    const videoRef = useRef(null);
    const buttonClose = useRef(null);
    const recognitionRef = useRef(null);
    const currentUser = getUser();
    const prevTranscript = useRef();

    useEffect(() => {

        setPoster(cloneData?.picture?.media || '/images/avatar/avatar-default.jpg')

        if (!("SpeechRecognition" in window || "webkitSpeechRecognition" in window)) {
            console.log("Il riconoscimento vocale non è supportato in questo browser.");
            return;
        }

        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        const userLang = navigator.language;

        recognitionRef.current = new SpeechRecognition();
        recognitionRef.current.continuous = false;
        recognitionRef.current.interimResults = true;
        recognitionRef.current.lang = userLang;

        recognitionRef.current.onstart = () => {
            if (!isCallEnded) {
                setFeedbackText("Listening");
            }
        };

        recognitionRef.current.onend = () => {
            if (!isCallEnded) {
                setFeedbackText("Thinking");
            }
        };

        recognitionRef.current.onresult = (event) => {
            let tempTranscript = "";
            for (let i = event.resultIndex; i < event.results.length; i++) {
                tempTranscript += event.results[i][0].transcript + " ";
            }
            setTranscript(tempTranscript.trim());
            setFeedbackText("Speaking");
        };

        recognitionRef.current.onerror = (event) => {
            console.error("Errore di riconoscimento vocale:", event.error);
            setIsListening(false);
        };

        return () => {
            if (recognitionRef.current) {
                recognitionRef.current.stop();
            }
        };
    }, [isCallEnded]);

    const fetchProfile = async () => {
        if (!profileData?.name) {
            try {
                const response = await ApiService.get(`/cms/profiles?author=${currentUser?._id}&name=${currentUser?.username}`);
                if (response?.contents) {
                    setProfileData(response?.contents[0]?.data);
                }
            } catch (error) {
                console.error("Error fetching profile:", error);
            }
        }
    };

    useEffect(() => {
        resetStates();
        setPoster(cloneData?.picture?.media || '/images/avatar/avatar-default.jpg')
    }, [cloneData])

    useEffect(() => {
        fetchProfile();
    }, [currentUser])

    useEffect(() => {
        const modal = document.getElementById("callingScreen");
        resetStates()

        if (modal) {
            modal.addEventListener("shown.bs.modal", handleModalOpen);
            modal.addEventListener("hidden.bs.modal", handleModalClose);
        }

        return () => {
            if (modal) {
                modal.removeEventListener("shown.bs.modal", handleModalOpen);
                modal.removeEventListener("hidden.bs.modal", handleModalClose);
            }
        };

    }, []);

    const handleModalOpen = () => {
        resetStates();
    };

    const handleModalClose = () => {
        resetStates();
        stopListening();
    };

    const resetStates = () => {
        setTranscript("");
        setMessages([]);
        setMessage('');
        setAudio('');
        setVideo('')
        setIsCallEnded(false)
        setFeedbackText('')
    };

    const startListening = () => {
        setAudio('')
        setVideo('')
        if (!recognitionRef.current || isCallEnded) return;
        if (isListening) {
            stopListening();
        }

        try {
            recognitionRef.current.start();
            setIsListening(true);
        } catch (error) {
            console.warn("Errore durante l'avvio del riconoscimento vocale:", error);
        }
    };

    const stopListening = () => {
        if (recognitionRef.current && isListening) {
            recognitionRef.current.stop();
            setIsListening(false);
        }
    };

    useEffect(() => {
        if (transcript !== 'undefined' && prevTranscript.current !== transcript && transcript.length > 0 && isListening == true) {
            prevTranscript.current = transcript;
        }
    }, [transcript]);

    useEffect(() => {
        if (isListening == false && transcript?.length > 0) {
            letsChat();
        }
    }, [isListening]);

    const letsChat = async () => {
        try {
            const payload = {
                message: transcript,
                context: '',
                instructions: Instructions(cloneData, profileData)
            };

            setMessages([...messages, { question: transcript }]);
            const chat = await ApiService.post(`/cms/ai/chat`, payload);
            setMessages(chat?.context || []);
            setMessage(chat?.context[0]?.answer || '');
            if (chat?.context && chat.context.length > 0) {
                letsTalk(chat.context[0]?.answer);
            }
        } catch (err) {
            console.error("Errore durante il fetch dei dati della chat:", err);
        }
    };

    const letsTalk = async (text) => {
        try {
            const talk = await getTalk(cloneData, text, cloneData?.picture?.media) || null;
            if (talk?.result_url) {
                setVideo(talk.result_url);
                setPoster(cloneData?.picture?.media || '/images/avatar/avatar-default.jpg')
            } else {
                setPoster('/images/call_ended.png')
                setIsCallEnded(true)
            }
        } catch (error) {
            console.error("Errore durante la generazione dell'audio:", error);
            setPoster('/images/call_ended.png')
            setIsCallEnded(true)
        }
    };

    useEffect(() => {
        if (video && videoRef.current && !isCallEnded) {
            setFeedbackText("Speaking");
            const videoElement = videoRef.current;

            videoElement.play().catch(error => console.error("Errore durante la riproduzione:", error));

            const handleVideoEnd = () => {
                setFeedbackText("");
            };

            videoElement.addEventListener("ended", handleVideoEnd);

            return () => {
                videoElement.removeEventListener("ended", handleVideoEnd);
            };
        }
    }, [video, isCallEnded]);

    const AnimatedDots = ({ text }) => {
        return (
            <div className="feedback-text tyn-media-row has-dot-sap animated-text">
                <span className="meta">{text}
                    <span className="dot-1">.</span>
                    <span className="dot-2">.</span>
                    <span className="dot-3">.</span>
                </span>
            </div>
        );
    };


    return (
        <div
            className="modal fade"
            tabIndex="-1"
            id="videoCallingScreen"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
        >
            <div className="modal-dialog modal-dialog-centered modal-sm">
                <div className="modal-content border-0">
                    <div className="tyn-chat-call tyn-chat-call-video">
                        <div className="tyn-chat-call-stack">
                            <div className="tyn-chat-call-cover">
                                <video
                                    className="w-100 h-100"
                                    src={video}
                                    ref={videoRef}
                                    poster={poster}
                                    autoPlay
                                    playsInline
                                    style={{ objectFit: 'cover', objectPosition: 'center', borderRadius: 'inherit' }}
                                />
                            </div>
                        </div>
                        <div className="tyn-chat-call-stack on-dark">
                            {!isCallEnded ? (
                                <div className="tyn-media-group p-4">
                                    <div className="tyn-media-col align-self-start pt-3">
                                        <div className="tyn-media-row has-dot-sap">
                                            <span className="meta">Talking With ...</span>
                                        </div>
                                        <div className="tyn-media-row">
                                            <h6 className="name">{cloneData?.name} {cloneData?.surname}</h6>
                                        </div>
                                        {!isCallEnded && feedbackText && (
                                            <AnimatedDots text={feedbackText} />
                                        )}
                                    </div>
                                    <div className="tyn-media tyn-media-1x1_3 tyn-size-3xl border border-2 border-dark">
                                        <img src={`${profileData?.picture?.media ? profileData?.picture?.media : '/images/avatar/avatar-default.jpg'}`} alt="User Avatar" />
                                    </div>
                                </div>
                            ) : (<></>)}

                            <ul className="tyn-list-inline gap gap-3 mx-auto py-4 justify-content-center mt-auto">
                                {!isCallEnded ? (
                                    <>
                                        <li>
                                            <button
                                                className="btn btn-icon btn-pill btn-light"
                                                data-bs-toggle="modal"
                                                data-bs-target="#callingScreen"
                                                onMouseEnter={() => setIsHovered(true)}
                                                onMouseLeave={() => setIsHovered(false)}
                                            >
                                                {isHovered ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-camera-video-off-fill" viewBox="0 0 16 16">
                                                        <path fillRule="evenodd" d="M10.961 12.365a2 2 0 0 0 .522-1.103l3.11 1.382A1 1 0 0 0 16 11.731V4.269a1 1 0 0 0-1.406-.913l-3.111 1.382A2 2 0 0 0 9.5 3H4.272zm-10.114-9A2 2 0 0 0 0 5v6a2 2 0 0 0 2 2h5.728zm9.746 11.925-10-14 .814-.58 10 14z" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-camera-video-fill" viewBox="0 0 16 16">
                                                        <path fillRule="evenodd" d="M0 5a2 2 0 0 1 2-2h7.5a2 2 0 0 1 1.983 1.738l3.11-1.382A1 1 0 0 1 16 4.269v7.462a1 1 0 0 1-1.406.913l-3.111-1.382A2 2 0 0 1 9.5 13H2a2 2 0 0 1-2-2z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </li>

                                        <li>
                                            <button
                                                className="btn btn-icon btn-pill btn-light"
                                                onMouseDown={startListening}
                                                onMouseUp={stopListening}
                                                onTouchStart={startListening}
                                                onTouchEnd={stopListening}
                                            >
                                                {isListening ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-soundwave" viewBox="0 0 16 16">
                                                        <path fillRule="evenodd" d="M8.5 2a.5.5 0 0 1 .5.5v11a.5.5 0 0 1-1 0v-11a.5.5 0 0 1 .5-.5m-2 2a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5m4 0a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5m-6 1.5A.5.5 0 0 1 5 6v4a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m8 0a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5m-10 1A.5.5 0 0 1 3 7v2a.5.5 0 0 1-1 0V7a.5.5 0 0 1 .5-.5m12 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0V7a.5.5 0 0 1 .5-.5" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-mic-fill" viewBox="0 0 16 16">
                                                        <path d="M5 3a3 3 0 0 1 6 0v5a3 3 0 0 1-6 0z" />
                                                        <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5" />
                                                    </svg>
                                                )}
                                            </button>
                                        </li>
                                    </>
                                ) : (<></>)}

                                <li>
                                    <button
                                        className="btn btn-icon btn-pill btn-danger"
                                        data-bs-dismiss="modal"
                                        ref={buttonClose}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="16"
                                            height="16"
                                            fill="#fff"
                                            className="bi bi-telephone-x-fill"
                                            viewBox="0 0 16 16"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M1.885.511a1.745 1.745 0 0 1 2.61.163L6.29 2.98c.329.423.445.974.315 1.494l-.547 2.19a.68.68 0 0 0 .178.643l2.457 2.457a.68.68 0 0 0 .644.178l2.189-.547a1.75 1.75 0 0 1 1.494.315l2.306 1.794c.829.645.905 1.87.163 2.611l-1.034 1.034c-.74.74-1.846 1.065-2.877.702a18.6 18.6 0 0 1-7.01-4.42 18.6 18.6 0 0 1-4.42-7.009c-.362-1.03-.037-2.137.703-2.877zm9.261 1.135a.5.5 0 0 1 .708 0L13 2.793l1.146-1.147a.5.5 0 0 1 .708.708L13.707 3.5l1.147 1.146a.5.5 0 0 1-.708.708L13 4.207l-1.146 1.147a.5.5 0 0 1-.708-.708L12.293 3.5l-1.147-1.146a.5.5 0 0 1 0-.708"
                                            />
                                        </svg>
                                    </button>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalVideocall;
